// Generic, column-configuration driven table renderer. A single column list
// produces both the head row and the data rows, replacing the previous
// pattern of separate, near-identical createXxxHead()/updateXxxRanking()
// switch-cascades per session type.
//
// Column shape: { key, header, headClass, cellClass, render, html, renderParts }
//   - header:      head cell text, or an array of strings to stack multiple
//                  lines in the header (matching a renderParts cell below it)
//   - headClass:   CSS class for the <th>
//   - cellClass:   CSS class for the <td>, string or (row) => string
//   - render:      (row) => value, defaults to row[key]
//   - html:        if true, value is set via innerHTML (only for trusted,
//                  server-composed markup like flag <img>/<br>, never for
//                  raw user-entered strings)
//   - renderParts: (row) => [{ text, className }, ...] - stacks multiple
//                  values in one cell (e.g. lap time above its penalty) as
//                  separate <div>s, still via textContent (no innerHTML)

export function renderHead(table, columns) {
  while (table.rows.length > 0) table.deleteRow(0);
  const row = table.insertRow(0);
  for (const col of columns) {
    const th = document.createElement("th");
    if (col.headClass) th.className = col.headClass;
    if (Array.isArray(col.header)) {
      th.replaceChildren(
        ...col.header.map((text) => {
          const div = document.createElement("div");
          div.textContent = text;
          return div;
        })
      );
    } else {
      th.textContent = col.header;
    }
    row.appendChild(th);
  }
}

// Updates table rows in place, keyed by rowKey, instead of clearing and
// rebuilding the whole table on every telegram: existing <tr>s are reused
// and only repositioned/re-filled, stale rows are removed, new rows appended.
// rowKey is either a field name (e.g. "STN") or a (row, index) => key
// function - needed for lists where the field alone isn't unique, e.g. the
// Scroll run history where the same STN appears once per run.
// options.highlightNew briefly flashes genuinely new rows (via the
// "row-flash" CSS class) - but never on a table's first-ever render, so a
// page reload or first switch to a tab doesn't flash every existing row.
export function renderRows(table, columns, rows, rowKey, options = {}) {
  const { highlightNew = false } = options;
  const isFirstRender = table.dataset.populated !== "true";
  const keyFor = typeof rowKey === "function" ? rowKey : (row) => row[rowKey];
  const existing = new Map();
  for (let i = 1; i < table.rows.length; i++) {
    const tr = table.rows[i];
    existing.set(tr.dataset.rowKey, tr);
  }

  let previous = table.rows[0] ?? null;
  const usedKeys = new Set();

  rows.forEach((rowData, index) => {
    const key = String(keyFor(rowData, index));
    usedKeys.add(key);
    let tr = existing.get(key);
    const isNewRow = !tr;
    if (!tr) {
      tr = table.insertRow(-1);
      tr.dataset.rowKey = key;
    }
    updateRowCells(tr, columns, rowData);

    if (isNewRow && highlightNew && !isFirstRender) {
      tr.classList.add("row-flash");
    }

    if (previous ? previous.nextElementSibling !== tr : table.rows[0] !== tr) {
      if (previous) {
        previous.after(tr);
      } else {
        table.prepend(tr);
      }
    }
    previous = tr;
  });

  for (const [key, tr] of existing) {
    if (!usedKeys.has(key)) tr.remove();
  }

  table.dataset.populated = "true";
}

function cellClassName(col, rowData) {
  return typeof col.cellClass === "function" ? col.cellClass(rowData) : col.cellClass || "";
}

function updateRowCells(tr, columns, rowData) {
  while (tr.cells.length > columns.length) tr.deleteCell(-1);
  columns.forEach((col, i) => {
    let cell = tr.cells[i];
    if (!cell) cell = tr.insertCell(-1);
    cell.className = cellClassName(col, rowData);

    if (col.renderParts) {
      const parts = col.renderParts(rowData).map((part) => {
        const div = document.createElement("div");
        if (part.className) div.className = part.className;
        div.textContent = part.text ?? "";
        return div;
      });
      cell.replaceChildren(...parts);
      return;
    }

    const value = col.render ? col.render(rowData) : rowData[col.key];
    if (col.html) {
      cell.innerHTML = value ?? "";
    } else {
      cell.textContent = value ?? "";
    }
  });
}

export function clearElement(el) {
  el.innerHTML = "";
}
